import { useState } from 'react'
import { useAuth } from '@astro/auth'
import { ApiRequestError } from '@astro/api'
import { loginSchema } from '@astro/validators'
import { useToast } from '../../context/ToastContext'
import { InputField, PasswordInput, MailIcon } from './AuthControls'
import { toFieldErrors } from './formErrors'
import ExternalPathSync from './ExternalPathSync'

/** Email + password sign-in view. WhatsApp in the path-sync block hands off to the OTP flow. */
export default function LoginForm({ prefillEmail = '', onSwitchToRegister, onSwitchToOtp, onForgotPassword }) {
  const { showToast } = useToast()
  const { loginWithPassword } = useAuth()

  const [form, setForm] = useState({ email: prefillEmail, password: '' })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event) => {
    event.preventDefault()
    const parsed = loginSchema.safeParse(form)
    if (!parsed.success) return setErrors(toFieldErrors(parsed.error.issues))

    setErrors({})
    setSubmitting(true)
    try {
      const user = await loginWithPassword(parsed.data)
      showToast(`Welcome back, ${user.name || 'seeker'}!`)
      // AuthProvider flips status to "authenticated"; LoginPage's effect follows the redirect.
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setErrors(toFieldErrors(err.error.details))
        showToast(err.error.message || 'Sign in failed. Please try again.')
      } else {
        showToast('Something went wrong. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div id="view-login-frame" className="auth-view-panel">
      <div className="space-y-2 mb-8 text-center sm:text-left">
        <h2 className="text-3xl font-royal tracking-wide text-astroDark uppercase font-medium">Welcome Back!</h2>
        <p className="text-[11px] uppercase font-bold tracking-[0.25em] text-amber-800/60">Spiritual Point Access</p>
        <p className="text-sm text-stone-500 pt-1">
          New Seeker?{' '}
          <button type="button" onClick={onSwitchToRegister} className="text-goldAccent font-semibold hover:underline outline-none focus:underline">Create account</button>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <InputField
          label="Email Address"
          labelClass="block text-[11px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-2"
          icon={<MailIcon />}
          type="email"
          id="login-email"
          autoComplete="email"
          required
          placeholder="seeker@path.com"
          value={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
          error={errors.email}
        />

        <div>
          <div className="flex justify-between items-baseline mb-2">
            <label className="block text-[11px] font-bold uppercase tracking-[0.18em] text-stone-600">Secure Pin</label>
            <button type="button"
              onClick={() => onForgotPassword(form.email)}
              className="text-[11px] uppercase font-bold tracking-wider text-stone-400 hover:text-goldAccent transition-colors outline-none focus:text-goldAccent">Forgot Password?</button>
          </div>
          <PasswordInput
            required
            placeholder="••••••••"
            autoComplete="current-password"
            error={errors.password}
            value={form.password}
            onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
          />
          {errors.password && (
            <p className="text-xs text-red-600 mt-1.5 tracking-wide">{errors.password}</p>
          )}
        </div>

        <button type="submit" disabled={submitting} className="shine-btn w-full bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-sm tracking-[0.22em] uppercase py-4 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 mt-2 disabled:opacity-60 disabled:cursor-not-allowed">
          {submitting ? 'Signing In…' : 'Sign In System →'}
        </button>
      </form>

      <ExternalPathSync
        onGoogle={() => showToast('Connecting secure Google login link...')}
        onWhatsApp={onSwitchToOtp}
      />
    </div>
  )
}
